import { Truck } from "lucide-react"
import type { FleetTruck } from "../data/types"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { getIntlLocale } from "@/lib/i18n/format"

interface FlotaTabProps {
  trucks: FleetTruck[]
}

const formatNumber = (value: number) => {
  return new Intl.NumberFormat(getIntlLocale("pl-PL")).format(value)
}

const formatTime = (hours: number) => {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  if (m === 0) return `${h} h`
  return `${h} h ${m} min`
}

const formatTimeDiff = (hours: number) => {
  const h = Math.floor(Math.abs(hours))
  const m = Math.round((Math.abs(hours) - h) * 60)
  const sign = hours >= 0 ? "+" : "-"
  if (m === 0) return `${sign}${h} h`
  return `${sign}${h} h ${m} min`
}

export function FlotaTab({ trucks }: FlotaTabProps) {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg border border-[#e5e5e5] bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#e5e5e5] text-left">
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.dispatchers")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.orders")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.revenue")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.distance")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.roadCosts")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.fuel")}
              <br />
              <sup>(l/100 km)</sup>
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.drivingTime")}
            </th>
            <th className="px-4 py-3 text-sm font-medium text-[#737373]">
              {t("planner.stats.metrics.loadedEmpty")}
            </th>
          </tr>
        </thead>
        <tbody>
          {trucks.map((truck) => (
            <tr
              key={truck.id}
              className="border-b border-[#e5e5e5] last:border-0"
            >
              {/* Truck + Driver */}
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <Truck className="h-4 w-4 text-[#737373]" />
                  <div>
                    <div className="text-sm font-medium text-[#0a0a0a]">
                      {truck.truckNumber}
                    </div>
                    <div className="text-xs font-medium text-[#2563eb]">
                      {truck.driverName}
                    </div>
                  </div>
                </div>
              </td>
              {/* Zlecenia */}
              <td className="px-4 py-3">
                <span className="text-sm font-medium text-[#2563eb]">
                  {t("planner.stats.orderCount", { count: truck.ordersCount })}
                </span>
              </td>
              {/* Przychod */}
              <td className="px-4 py-3 text-sm text-[#0a0a0a]">
                {formatNumber(truck.revenue)} €
              </td>
              {/* Dystans */}
              <td className="px-4 py-3">
                <div className="text-sm text-[#0a0a0a]">
                  {formatNumber(truck.distance)} km
                </div>
                <div
                  className={cn(
                    "text-xs font-medium",
                    truck.distanceDiff > 0
                      ? "text-[#dc2626]"
                      : "text-[#15803d]",
                  )}
                >
                  {truck.distanceDiff > 0 ? "+" : ""}
                  {formatNumber(truck.distanceDiff)} km
                </div>
              </td>
              {/* Oplaty drogowe */}
              <td className="px-4 py-3">
                <div className="text-sm text-[#0a0a0a]">
                  {formatNumber(truck.roadCosts)} €
                </div>
                <div
                  className={cn(
                    "text-xs font-medium",
                    truck.roadCostsDiff > 0
                      ? "text-[#dc2626]"
                      : "text-[#15803d]",
                  )}
                >
                  {truck.roadCostsDiff > 0 ? "+" : ""}
                  {formatNumber(truck.roadCostsDiff)} €
                </div>
              </td>
              {/* Paliwo */}
              <td className="px-4 py-3">
                <div className="text-sm text-[#0a0a0a]">{truck.fuel} l</div>
                {truck.fuelDiff !== 0 && (
                  <div
                    className={cn(
                      "text-xs font-medium",
                      truck.fuelDiff > 0 ? "text-[#dc2626]" : "text-[#15803d]",
                    )}
                  >
                    {truck.fuelDiff > 0 ? "+" : ""}
                    {truck.fuelDiff} l
                  </div>
                )}
              </td>
              {/* Czas jazdy */}
              <td className="px-4 py-3">
                <div className="text-sm text-[#0a0a0a]">
                  {formatTime(truck.drivingTime)}
                </div>
                <div
                  className={cn(
                    "text-xs font-medium",
                    truck.drivingTimeDiff > 0
                      ? "text-[#dc2626]"
                      : "text-[#15803d]",
                  )}
                >
                  {formatTimeDiff(truck.drivingTimeDiff)}
                </div>
              </td>
              {/* Ladowne / Nieladowne */}
              <td className="px-4 py-3">
                <div className="text-sm text-[#0a0a0a]">
                  {formatNumber(truck.loadedKm)} km /{" "}
                  {formatNumber(truck.emptyKm)} km
                </div>
                <div className="text-xs text-[#737373]">
                  {truck.loadedPercent}% / {truck.emptyPercent}%
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
